import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { i18n } from 'src/i18n';
import {
  default as authSelectors,
  default as selectors,
} from 'src/modules/auth/authSelectors';
import PermissionChecker from 'src/modules/auth/permissionChecker';
import layoutActions from 'src/modules/layout/layoutActions';
import actions from 'src/modules/layout/layoutActions';
import layoutSelectors from 'src/modules/layout/layoutSelectors';
import menus from 'src/view/menus';

function Menu(props) {
  const dispatch = useDispatch();

  const logoUrl = useSelector(selectors.selectLogoUrl);

  const currentTenant : any = useSelector(
    authSelectors.selectCurrentTenant,
  );
  const currentUser : any = useSelector(
    authSelectors.selectCurrentUser,
  );
  const menuVisible = useSelector(
    layoutSelectors.selectMenuVisible,
  );

  const hasPermissions : boolean = currentUser && currentUser.permissions;
  const permissions = currentUser.permissions;
  const isPartner : boolean = permissions && permissions.isPartner;
  const hasBillingAccess : boolean = currentUser?.hasBillingAccess ?? true;
  const isRootPartner : boolean = currentUser?.isRootPartner ?? false;

  const doToggleMenuIfSmall = () => {
    if (window.innerWidth < 640) {
      dispatch(layoutActions.doToggleMenu());
    }
  };

  const permissionChecker = new PermissionChecker(
    currentTenant,
    currentUser,
  );

  useLayoutEffect(() => {
    const toggleMenuOnResize = () => {
      window.innerWidth < 1028
        ? dispatch(actions.doHideMenu())
        : dispatch(actions.doShowMenu());
    };

    toggleMenuOnResize();

    window.addEventListener('resize', toggleMenuOnResize);

    return () => {
      window.removeEventListener(
        'resize',
        toggleMenuOnResize,
      );
    };
  }, [dispatch]);

  const selectedKeys = () => {
    const url = props.url;

    const match = menus.find((option) => {
      if (option.exact) {
        return url === option.path;
      }

      return (
        url === option.path ||
        url.startsWith(option.path + '/')
      );
    });

    if (match) {
      return [match.path];
    }

    return [];
  };

  const match = (permission) => {
    return permissionChecker.match(permission);
  };

  const lockedForCurrentPlan = (permission) => {
    return permissionChecker.lockedForCurrentPlan(
      permission,
    );
  };

  return (
    <div
      className={`${
        menuVisible ? 'fixed inset-0 flex flex-col w-full z-[100] sm:hidden' : 'hidden'
      } md:flex md:relative md:w-20 lg:w-20 md:inset-auto flex-shrink-0 min-h-screen px-2 py-4 border-0 md:border-r lg:border-r bg-gray-800 dark:border-gray-600 overflow-visible z-50`}
    >
      {/* Close button for mobile - only shows on small screens */}
      <div className="flex justify-end md:hidden mb-4">
        <button
          className="text-gray-400 hover:text-white text-2xl p-2"
          onClick={doToggleMenuIfSmall}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* <div className="w-full flex justify-between sm:justify-center md:justify-center lg:justify-center items-center">
        <Link onClick={doToggleMenuIfSmall} to="/">
          {logoUrl ? (
            <img
              src={logoUrl}
              className="w-72 max-h-14 object-cover"
              alt={i18n('app.title')}
            />
          ) : (
            <div className="text-center text-3xl font-semibold text-gray-200 hover:text-white">
              <>{i18n('app.title')}</>
            </div>
          )}
        </Link>
        <div className="cursor-pointer block sm:hidden md:hidden lg:hidden text-gray-400 hover:text-gray-200 text-lg mr-2">
          <FontAwesomeIcon
            onClick={doToggleMenuIfSmall}
            icon={faTimes}
          />
        </div>
      </div> */}

      <div className="flex flex-col justify-between flex-1 mt-6">
        <nav>
          {menus
            .filter((menu) =>
              isPartner || match(menu.permissionRequired),
            )
            .filter((menu) =>
              // Hide billing-related items if user doesn't have billing access
              !menu.requiresBillingAccess || hasBillingAccess
            )
            .filter((menu) =>
              // Hide root partner only items if user is not a root partner
              !menu.requiresRootPartner || isRootPartner
            )
            .map((menu, index) => {
              // For partner users, show all items regardless of permissionType
              // For non-partner users, check if they have the specific permission
              const shouldShow = index === 0 ||
                isPartner ||
                (hasPermissions && menu.permissionType && permissions[menu.permissionType]);

              if (shouldShow) {
                if (menu.label === 'Max Bids' && !isPartner) return '';
                return (
                <Link
                  className={`${index !== 0 ? 'mt-4' : ''} ${
                    selectedKeys().includes(menu.path)
                      ? 'flex items-center px-3 py-2 rounded-md bg-gray-700 text-gray-200'
                      : 'flex items-center px-3 py-2 transition-colors duration-200 transform rounded-md text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                  } relative group sm:justify-center md:justify-center`}
                  onClick={doToggleMenuIfSmall}
                  key={menu.path}
                  to={menu.path}
                  title={menu.label}
                >
                  <FontAwesomeIcon
                    className="w-9 h-9 flex-shrink-0"
                    icon={menu.icon}
                  />
                  {/* Label visible on mobile, hidden on desktop */}
                  <span className="ml-3 text-base font-medium sm:hidden">
                    {menu.label}
                  </span>
                  {/* Tooltip on hover - only visible on desktop */}
                  <span className="hidden md:block absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-[9999]">
                    {menu.label}
                  </span>
                </Link>
              )}
              return '';
            })}

          {/* Only show locked items for non-partner users */}
          {!isPartner && menus
            .filter((menu) =>
              lockedForCurrentPlan(menu.permissionRequired),
            )
            .map((menu) => (
              <div
                key={menu.path}
                className={`mt-4 opacity-50 flex items-center px-4 py-3 text-gray-600 rounded-md dark:text-gray-400 relative group sm:justify-center md:justify-center`}
                title={menu.label}
              >
                <FontAwesomeIcon
                  className="w-7 h-7 flex-shrink-0"
                  icon={menu.icon}
                />
                {/* Label visible on mobile, hidden on desktop */}
                <span className="ml-3 text-base font-medium sm:hidden">
                  {menu.label}
                </span>
                {/* Tooltip on hover - only visible on desktop */}
                <span className="hidden md:block absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-[9999]">
                  {menu.label}
                </span>
              </div>
            ))}
        </nav>

        {/* Privacy Policy Link at Bottom */}
        {/* <div className="mt-auto pt-4 border-t border-gray-700">
          <Link
            className="flex items-center px-4 py-2 text-xs text-gray-400 hover:text-gray-200 transition-colors duration-200"
            onClick={doToggleMenuIfSmall}
            to="/privacy-policy"
          >
            Privacy Policy
          </Link>
        </div> */}
      </div>
    </div>
  );
}

export default Menu;
